import React, { useState, useEffect } from 'react';
import {
  Form,
  Input,
  Button,
  Upload,
  Card,
  Space,
  message,
  ColorPicker,
  Typography,
} from 'antd';
import { PlusOutlined } from '@ant-design/icons';
import { apiRequests } from "../../shared/api/api";

const { Title, Text } = Typography;

// Превью карты Apple Wallet по схеме
const AppleWalletPreview = ({ settings }) => {
  const {
    logo_text = '',
    barcode_message = '',
    colors = {},
    logo_path,
    strip_path,
  } = settings;

  const fg = colors.foregroundColor || '#000000';
  const bg = colors.backgroundColor || '#FFFFFF';
  const labelColor = colors.labelColor || '#888888';

  return (
    <div
      style={{
        width: 320,
        borderRadius: 20,
        overflow: 'hidden',
        boxShadow: '0 4px 12px rgba(0,0,0,0.2)',
        backgroundColor: bg,
        color: fg,
        position: 'relative',
        fontFamily: '-apple-system, BlinkMacSystemFont, sans-serif',
        padding: '10px',
        boxSizing: 'border-box',
      }}
    >
      {/* Header: Logo + Logo text */}
      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 12 }}>
        {logo_path && (
          <img
            src={`https://${process.env.REACT_APP_APP_URL}/api/v1/${logo_path}/`}
            alt="logo"
            style={{ height: 36, marginRight: 8 }}
          />
        )}
        <div style={{ fontSize: 16, fontWeight: 'bold', color: fg }}>{logo_text}</div>
        {/* Header Fields (заглушка) */}
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            backgroundColor: bg,
            borderRadius: 8,
          }}
        >
          <div style={{ fontSize: 12, margin: 0, marginRight: 8, padding: '8px 0px' }}>
            <div style={{ color: labelColor, }}>БАЛАНС</div>
            <div style={{ fontSize: 16, color: fg, display: 'flex', justifyContent: 'right', }}>...</div>
          </div>
          <div style={{ fontSize: 12, color: labelColor, padding: '8px 0px' }}>
            <div style={{ color: labelColor }}>БОНУСЫ</div>
            <div style={{ fontSize: 16, color: fg, display: 'flex', justifyContent: 'right', }}>...</div>
          </div>
        </div>
      </div>
      {/* Strip */}
      {strip_path && (
        <img
          src={`https://${process.env.REACT_APP_APP_URL}/api/v1/${strip_path}/`}
          alt={`strip`}
          style={{
            width: '100%',        // занять всю ширину контейнера
            height: 60,           // фиксированная высота
            objectFit: 'cover',   // увеличить и обрезать излишки
            objectPosition: 'center', // позиционирование обрезки
            marginBottom: 0,
            display: 'block'      // убрать возможные пробелы под изображением
          }}
        />
      )}

      {/* Secondary & Auxiliary Fields */}
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          marginBottom: 50,
          padding: '8px 12px',
        }}
      >
        <div style={{ fontSize: 12 }}>
          <div style={{ color: labelColor }}>ВЛАДЕЛЕЦ КАРТЫ</div>
          <div style={{ fontSize: 16, color: fg }}>...</div>
        </div>
        <div style={{ fontSize: 12 }}>
          <div style={{ color: labelColor }}>НОМЕР КАРТЫ</div>
          <div style={{ fontSize: 16, color: fg }}>...</div>
        </div>
      </div>

      {/* Barcode */}
      <div style={{ justifyContent: 'center', display: 'flex' }} >
        <div
          style={{
            textAlign: 'center',
            marginTop: 12,
            borderRadius: 4,
            backgroundColor: '#ffffff',
          }}
        >
          <div style={{ fontSize: 12, marginBottom: 4, padding: '6px 6px 0px 6px', }}>
            <img
              src={`https://${process.env.REACT_APP_APP_URL}/api/v1/photos/AppleWalletQRExample.png/`}
              alt="barcode"
              style={{ height: 72, marginRight: 0 }}
            />
          </div>
          <div
            style={{
              height: 30,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontSize: 10,
              color: '#000000',
              padding: '0px 6px 6px 6px'
            }}
          >
            {barcode_message}
          </div>
        </div>
      </div>
    </div>
  );
};

const CreateAppleWalletPassForm = () => {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [uploadLoading, setUploadLoading] = useState({});

  // Загрузка текущих настроек
  useEffect(() => {
    const fetchSettings = async () => {
      try {
        const res = await apiRequests.apple_card_settings.get_settings();
        const data = res.data;
        form.setFieldsValue({
          logo_text: data.logo_text || '',
          description: data.description || '',
          barcode_message: data.barcode_message || 'TableCRM',
          icon_path: data.icon_path || '',
          logo_path: data.logo_path || '',
          strip_path: data.strip_path || '',
          colors: data.colors || {},
        });
      } catch (err) {
        console.error('Failed to load Apple Wallet settings', err);
        message.error('Не удалось загрузить настройки');
      }
    };

    fetchSettings();
  }, [form]);

  // === Загрузка файлов ===
  const handleUpload = async (file, field) => {
    setUploadLoading(prev => ({ ...prev, [field]: true }));

    try {
      const res = await apiRequests.apple_card_settings.upload_file(file);
      const filePath = res.data.path;
      form.setFieldsValue({ [field]: filePath });
      message.success('Файл успешно загружен');
    } catch (err) {
      console.error('Upload error:', err);
      message.error('Ошибка загрузки файла');
    } finally {
      setUploadLoading(prev => ({ ...prev, [field]: false }));
    }

    return false; // Prevent default upload behavior
  };

  // === Сохранение ===
  const onFinish = async (values) => {
    setLoading(true);
    try {
      const payload = {
        ...values,
        locations: [], // Всегда пустой массив
      };
      await apiRequests.apple_card_settings.patch_settings(payload);
      message.success('Настройки успешно сохранены');
    } catch (err) {
      console.error('Save error:', err);
      message.error('Не удалось сохранить настройки');
    } finally {
      setLoading(false);
    }
  };

  // Получаем текущие значения для превью
  const currentValues = Form.useWatch([], form);

  return (
    <div style={{ display: 'flex', gap: 24, flexWrap: 'wrap' }}>
      {/* Превью вверху */}
      <div style={{ width: '100%', display: 'flex', justifyContent: 'center' }}>
        <AppleWalletPreview settings={currentValues || {}} />
      </div>

      {/* Форма */}
      <Card title="Настройки Apple Wallet Pass" style={{ flex: 1, minWidth: 400 }}>
        <Form
          form={form}
          layout="vertical"
          onFinish={onFinish}
          autoComplete="off"
        >
          <Form.Item name="logo_text" label="Текст логотипа">
            <Input placeholder="Название программы (например, TableCRM)" />
          </Form.Item>

          <Form.Item name="barcode_message" label="Сообщение штрихкода">
            <Input placeholder="Код, номер или текст под штрихкодом" />
          </Form.Item>

          {/* Цвета */}
          <Title level={5}>Цвета</Title>
          <Form.Item
            name={['colors', 'foregroundColor']}
            label="Цвет текста"
            getValueFromEvent={(c) => c.toHexString()}
          >
            <ColorPicker showText />
          </Form.Item>

          <Form.Item
            name={['colors', 'backgroundColor']}
            label="Фоновый цвет"
            getValueFromEvent={(c) => c.toHexString()}
          >
            <ColorPicker showText />
          </Form.Item>

          <Form.Item
            name={['colors', 'labelColor']}
            label="Цвет меток"
            getValueFromEvent={(c) => c.toHexString()}
          >
            <ColorPicker showText />
          </Form.Item>

          {/* Изображения */}
          <Title level={5}>Изображения</Title>

          {['icon_path', 'logo_path', 'strip_path'].map((field) => {
            const labelMap = {
              icon_path: 'Иконка (logo, 29×29 pt)',
              logo_path: 'Логотип (logo, 160×50 pt)',
              strip_path: 'Полоса (strip, 624×220 pt)',
            };

            const filePath = form.getFieldValue(field);
            const fileName =
              typeof filePath === 'string' && filePath ? filePath.split('/').pop() : null;

            return (
              <Form.Item key={field} label={labelMap[field]} name={field}>
                <Upload
                  beforeUpload={(f) => handleUpload(f, field)}
                  showUploadList={false}
                  accept="image/*"
                >
                  <Button
                    icon={<PlusOutlined />}
                    loading={uploadLoading[field]}
                  >
                    Загрузить
                  </Button>
                </Upload>
                {fileName && (
                  <div style={{ marginTop: 8 }}>
                    <Text type="secondary" style={{ fontSize: 14 }}>
                      {fileName}
                    </Text>
                  </div>
                )}
              </Form.Item>
            );
          })}

          <Form.Item style={{ marginTop: 24 }}>
            <Space>
              <Button type="primary" htmlType="submit" loading={loading}>
                Сохранить
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Card>
    </div>
  );
};

export default CreateAppleWalletPassForm;